'use client';
import articleIcon from '@/public/images/article_icon.svg';
import {
  SEARCH_SUGGESTIONS_API_DEBOUNCE_TIME,
  supportBasePath,
} from '@/utils/constants';
import {GTMSearchSubmitClickEventTrack} from '@/utils/gtm';
import type {Article} from '@/utils/kms/article.types';
import {getPublicItem} from '@/utils/kms/get-public-item/route';
import {searchSuggestions} from '@/utils/kms/search-query/route';
import type {
  SearchSuggestionItem,
  SearchSuggestions,
} from '@/utils/kms/search-query/types';
import {formatTitleForURL} from '@/utils/utils';
import {Paper, type PaperProps, TextField} from '@mui/material';
import InputAdornment from '@mui/material-next/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import {CancelOutlinedIcon, SearchOutlinedIcon} from '@pixel-party/Icon';
import Image from 'next/image';
import {useRouter, useSearchParams} from 'next/navigation';
import type React from 'react';
import {useState} from 'react';
import {useDebouncedCallback} from 'use-debounce';
import styles from './Search.module.scss';

const DOCUMENT_ITEM_SUB_TYPES = [
  'T_HOW_TO',
  'T_SERVICE_PRODUCT',
  'T_INTERNAL_SERVICE_PRODUCT',
];

function CustomPaper({children}: PaperProps) {
  return (
    <Paper
      elevation={4}
      className={styles.customPaper}
    >
      {children}
    </Paper>
  );
}

export default function SearchBox({
  isClosed,
  onStateChange,
  locale,
  placeHolderText,
}: {
  locale: string;
  isClosed?: boolean;
  // eslint-disable-next-line no-unused-vars
  onStateChange?: (newValue: boolean) => void;
  placeHolderText?: string;
}) {
  const slugEnable = process.env.NEXT_PUBLIC_FEATURE_FLAG_SLUG_ENABLE;
  const [searchOptions, setSearchOptions] = useState<SearchSuggestionItem[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const searchParams = useSearchParams();
  const queryValue = searchParams?.get('q');
  const [value, setValue] = useState<string>(queryValue ?? '');
  const [inputValue, setInputValue] = useState<string>('');

  const router = useRouter();

  const handleInputChange = useDebouncedCallback(async (event, newValue) => {
    try {
      if (newValue.toString().length > 2) {
        const suggestionsResponse: SearchSuggestions = await searchSuggestions(
          newValue,
          0,
          10,
          locale,
        );
        setSearchOptions(suggestionsResponse?.items ?? []);
      } else {
        setSearchOptions([]);
      }
    } catch (error) {
      throw new Error(`search handleInputChange', ${error}`);
    }
  }, SEARCH_SUGGESTIONS_API_DEBOUNCE_TIME);
  const handleClick = () => {
    if (onStateChange) {
      onStateChange(!isClosed);
    }
  };

  const getslugTitle = async (articleId: string) => {
    const articleItem: Article = await getPublicItem(articleId, 'en');
    return articleItem?.title;
  };

  function isArticleType(subType: string) {
    return (
      subType && DOCUMENT_ITEM_SUB_TYPES.indexOf(subType.toUpperCase()) > -1
    );
  }
  return (
    <div className={styles.searchBoxWrap}>
      <Autocomplete
        data-testid="autocomplete-search-free-solo"
        freeSolo
        className={styles.searchBox}
        clearIcon={
          inputValue ? (
            <CancelOutlinedIcon className={styles.cancelIcon} />
          ) : (
            false
          )
        }
        onBlur={handleClick}
        id="free-solo-2-demo"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => setOpen(false)}
        renderInput={params => (
          <TextField
            placeholder={placeHolderText}
            {...params}
            className={styles.searchBoxTextField}
            InputProps={{
              ...params.InputProps,
              type: 'search',
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
              onKeyDown: e => {
                if (e.key === 'Enter') {
                  const newValue = (e?.target as any).value;
                  if (newValue) {
                    GTMSearchSubmitClickEventTrack('regular', `${newValue}`);
                    e.preventDefault();
                    e.stopPropagation();
                    setValue(newValue);
                    setOpen(false);
                    router.replace(
                      `${supportBasePath}/${locale}/search?q=${encodeURIComponent(newValue)}`,
                    );
                    router.refresh();
                  }
                }
              },
              className: styles.searchSuggestionTitle,
            }}
          />
        )}
        onInputChange={handleInputChange}
        options={searchOptions}
        value={value}
        filterOptions={x => x}
        onChange={async (event: React.SyntheticEvent | Event, option) => {
          const newValue = (option as SearchSuggestionItem)?.value ?? '';
          setValue(newValue);
          const articleId = (option as SearchSuggestionItem)?.label?.linkId;
          const subType = (option as SearchSuggestionItem)?.label?.subType;
          const articleTitle = await getslugTitle(articleId);
          const slugTitle = formatTitleForURL(articleTitle);
          GTMSearchSubmitClickEventTrack('auto-complete', `${articleTitle}`);
          if (newValue && articleId && subType) {
            if (isArticleType(subType)) {
              router.replace(
                `${supportBasePath}/${locale}/answer/${encodeURIComponent(articleId)}/${slugEnable === 'true' ? slugTitle : ''}`,
              );
            } else {
              router.replace(
                `${supportBasePath}/${locale}/search?q=${encodeURIComponent(newValue)}/${slugEnable === 'true' ? slugTitle : ''}`,
              );
            }
            router.refresh();
          }
          setInputValue((event.target as HTMLInputElement).value);
        }}
        onInput={event => {
          setInputValue((event.target as HTMLInputElement).value);
        }}
        getOptionLabel={option =>
          (option as SearchSuggestionItem)?.value ?? option
        }
        renderOption={(props, option) => {
          const {key, ...optionProps} = props;
          return (
            <li
              key={key}
              {...optionProps}
            >
              {isArticleType(option?.label?.subType) ? (
                <Image
                  src={articleIcon}
                  alt={option?.label.title}
                  width={24}
                  height={24}
                />
              ) : (
                <SearchOutlinedIcon
                  width={24}
                  height={24}
                />
              )}
              <span className={styles.searchSuggestionItemTitle}>
                {option?.label.title}
              </span>
            </li>
          );
        }}
        PaperComponent={CustomPaper}
      />
    </div>
  );
}
